import { MessageThreadProps } from '@azure/communication-react';

// Characters that are invalid in a CSS id selector (`#...`). FluentUI's AttachmentCard
// injects `attachment.id` verbatim into a `#attachment-<id>` querySelector, so an id
// containing spaces, dots, `%`, etc. (e.g. an upload id built from a URL-encoded filename)
// throws `Failed to execute 'querySelector'` and crashes the whole MessageThread on hover.
const CSS_ID_UNSAFE = /[^A-Za-z0-9_-]/g;

/** Make an attachment id safe to use inside a `#attachment-<id>` CSS selector. */
export const sanitizeAttachmentId = (id: string): string => id.replace(CSS_ID_UNSAFE, '-');

/**
 * Return `messages` with every chat-message attachment id replaced by a CSS-selector-safe
 * value. Messages without attachments (and attachments whose id is already safe) are
 * returned by reference, so MessageThread's per-row memoization is preserved. This also
 * repairs historical messages that were sent with an unsafe id.
 */
export const sanitizeMessageAttachmentIds = (
    messages: MessageThreadProps['messages'],
): MessageThreadProps['messages'] => {
    if (!messages) {
        return messages;
    }

    return messages.map((message) => {
        if (message.messageType !== 'chat' || !message.attachments?.length) {
            return message;
        }

        let changed = false;
        const attachments = message.attachments.map((attachment) => {
            const safeId = sanitizeAttachmentId(attachment.id);
            if (safeId === attachment.id) {
                return attachment;
            }
            changed = true;
            return { ...attachment, id: safeId };
        });

        return changed ? { ...message, attachments } : message;
    });
};
